import axios from 'axios';

function getPhotosBaseUrl() {
    return process.env.REACT_APP_PHOTOS_URL;
}

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL
});

const authTokenKey = 'auth_token';
const apiUnavailableReasonKey = 'api_unavailable_reason';

function setAuthToken(token, navigate) {
    localStorage.setItem(authTokenKey, token);
    window.dispatchEvent(new Event('auth-token-changed'));

    navigate('/');
}

function getAuthToken() {
    return localStorage.getItem(authTokenKey);
}

function isAuthenticated() {
    return !!localStorage.getItem(authTokenKey);
}

function logout() {
    localStorage.removeItem(authTokenKey);
    window.dispatchEvent(new Event('auth-token-changed'));

    window.location.href = '/login';
}

function redirectToUnauthorized() {
    window.location.href = '/unauthorized';
}

function redirectToNotFound() {
    window.location.href = '/not-found';
}

function redirectToApiUnavailable() {
    if (window.location.pathname === '/api-unavailable') {
        return;
    }

    window.location.href = '/api-unavailable';
}

function attachRequestMeta(err, method, path) {
    if (!err || typeof err !== 'object') {
        return err;
    }

    err.__requestMeta = {
        method,
        path,
    };

    return err;
}

function getRequestLabel(err) {
    const method = err?.__requestMeta?.method;
    const path = err?.__requestMeta?.path;

    if (!method || !path) {
        return '';
    }

    return `${method} ${path}`;
}

function getApiFailureReason(err) {
    const statusCode = getStatusCode(err);

    if (statusCode === 401) {
        return 'Sessão expirada ou não autorizada.';
    }

    if (statusCode === 403) {
        return 'Acesso negado para o recurso solicitado.';
    }

    if (statusCode === 404) {
        return 'Recurso não encontrado no servidor.';
    }

    if (statusCode === 422) {
        return 'Falha de validação nos dados enviados.';
    }

    if (statusCode >= 500) {
        return 'Erro interno no servidor.';
    }

    if (statusCode >= 400) {
        return `Erro HTTP ${statusCode}.`;
    }

    if (err?.code === 'ECONNABORTED') {
        return 'Tempo limite excedido na comunicação com o servidor.';
    }

    if (!navigator.onLine) {
        return 'Sem ligação à Internet no momento.';
    }

    return 'Falha de rede ao comunicar com o servidor.';
}

function buildApiUnavailableReason(err) {
    const requestLabel = getRequestLabel(err);
    const reason = getApiFailureReason(err);

    if (requestLabel) {
        return `${reason} Requisição: ${requestLabel}.`;
    }

    return reason;
}

function persistApiUnavailableReason(reason) {
    if (!reason) {
        return;
    }

    try {
        sessionStorage.setItem(apiUnavailableReasonKey, reason);
    } catch {
        // Best effort only.
    }
}

function clearApiUnavailableReason() {
    try {
        sessionStorage.removeItem(apiUnavailableReasonKey);
    } catch {
        // Best effort only.
    }
}

function getStatusCode(err) {
    const statusCode = err?.response?.status ?? err?.status ?? err?.request?.status;

    if (Number.isInteger(statusCode) && statusCode > 0) {
        return statusCode;
    }

    return undefined;
}

async function login(path, data, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {},) {
    await api.post(path, data, {
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(200, response, onSuccess, onError, onFinally);
        onFinally();
    })
    .catch((err) => {
        attachRequestMeta(err, 'POST', path);
        const statusCode = getStatusCode(err);

        if (statusCode === 401) {
            setMessage({ type: 'warning', text: 'Usuário ou senha incorretos.' });
            onFinally();
            return;
        }

        handleApiError(err, setMessage, onError, onFinally, 'none', 'redirect');
    });
}

async function post(path, data, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {}, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    await api.post(path, data, {
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(200, response, onSuccess, onError, onFinally);
        onFinally();
    })
    .catch((err) => {
        attachRequestMeta(err, 'POST', path);
        handleApiError(err, setMessage, onError, onFinally, unauthorizedAction, notFoundAction, networkFailureAction);
    });
}

    async function create(path, data, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {}, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    await api.post(path, data, {
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(201, response, onSuccess, onError, onFinally);
    })
    .catch((err) => {
        attachRequestMeta(err, 'POST', path);
        handleApiError(err, setMessage, onError, onFinally, unauthorizedAction, notFoundAction, networkFailureAction);
    });
}

    async function put(path, data, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {}, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    await api.put(path, data, {
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(200, response, onSuccess, onError, onFinally);
    })
    .catch((err) => {
        attachRequestMeta(err, 'PUT', path);
        handleApiError(err, setMessage, onError, onFinally, unauthorizedAction, notFoundAction, networkFailureAction);
    });
}

    async function remove(path, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {}, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    await api.delete(path, {
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(200, response, onSuccess, onError, onFinally);
    })
    .catch((err) => {
        attachRequestMeta(err, 'DELETE', path);
        handleApiError(err, setMessage, onError, onFinally, unauthorizedAction, notFoundAction, networkFailureAction);
    });
}

    async function get(path, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {}, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    await api.get(path, {
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(200, response, onSuccess, onError, onFinally);
    })
    .catch((err) => {
        attachRequestMeta(err, 'GET', path);
        handleApiError(err, setMessage, onError, onFinally, unauthorizedAction, notFoundAction, networkFailureAction);
    });
}

    async function getBlob(path, authorized, setMessage = () => {}, onSuccess, onError = () => {}, onFinally = () => {}, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    await api.get(path, {
        responseType: 'blob',
        headers: handleApiHeaders(authorized)
    })
    .then((response) => {
        handleApiResponse(200, response, onSuccess, onError, onFinally);
    })
    .catch((err) => {
        attachRequestMeta(err, 'GET', path);
        handleApiError(err, setMessage, onError, onFinally, unauthorizedAction, notFoundAction, networkFailureAction);
    });
}

function handleApiHeaders(authorized) {
   let headers = {};
    if (!!authorized)
        headers.Authorization = `Bearer ${getAuthToken()}`
    
    return headers;
}

function handleApiResponse(successStatus, response, onSuccess, onError, onFinally) {
    try {
        if (response.status !== successStatus) {
            onError('O servidor não retornou um status esperado.');
            return;
        }

        onSuccess(response);
        onFinally();
    } catch (err) {
        onFinally();
        onError(err);
    }
}

function handleApiError(err, setMessage, onError, onFinally, unauthorizedAction = 'logout', notFoundAction = 'redirect', networkFailureAction = 'api-unavailable') {
    const statusCode = getStatusCode(err);
    const requestLabel = getRequestLabel(err);

    const isNetworkFailure = !statusCode && !err?.response && err?.code !== 'ERR_CANCELED';
    if (isNetworkFailure) {
        const reason = buildApiUnavailableReason(err);
        persistApiUnavailableReason(reason);
        console.error('[API] Falha de rede', {
            reason,
            request: requestLabel || 'desconhecido',
            code: err?.code,
            message: err?.message,
        });
        setMessage({ type: 'error', text: 'Não foi possível comunicar com o servidor.' });
        onFinally();

        if (networkFailureAction === 'unauthorized') {
            redirectToUnauthorized();
            return;
        }

        redirectToApiUnavailable();
        return;
    }

    if (statusCode === 422) {
        clearApiUnavailableReason();
        setMessage({ type: 'warning', text: err.response.data[0].message });
        onFinally();
        return;
    }

    clearApiUnavailableReason();
    console.error('[API] Requisição falhou', {
        request: requestLabel || 'desconhecido',
        statusCode,
        code: err?.code,
        message: err?.message,
        response: err?.response?.data,
    });
    
    if (statusCode === 401) {
        if (unauthorizedAction === 'redirect') {
            onFinally();
            redirectToUnauthorized();
            return;
        }

        if (unauthorizedAction === 'logout') {
            onFinally();
            logout();
            return;
        }
    }

    if (statusCode === 403 && unauthorizedAction !== 'none') {
        onFinally();
        redirectToUnauthorized();
        return;
    }

    if (statusCode === 404 && notFoundAction === 'redirect') {
        onFinally();
        redirectToNotFound();
        return;
    }

    onError(err);
    onFinally();
}

export { setAuthToken, isAuthenticated, logout, login, create, post, put, remove, get, getBlob, getPhotosBaseUrl };